import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { getToken } from '../../../lib/auth'
import { SettingsButton, SettingsCard, SettingsChip, SettingsTitle } from '../../../components/settings/SettingsUI'
import GiftToast from '../../../components/gifts/GiftToast'
import {
  getMyCreatorSubscriptionSettings,
  patchMyCreatorSubscriptionSettings,
  type CreatorSubscriptionSettings,
  listMyCreatorTiers,
  createMyCreatorTier,
  patchMyCreatorTier,
  deleteMyCreatorTier,
  type CreatorTier,
} from '../../../api/creatorSubscriptions'
import {
  addCreatorEmailContact,
  exportCreatorEmailAudienceCsv,
  getCreatorEmailAudienceSummary,
  importCreatorEmailAudience,
  listCreatorEmailCampaigns,
  queueCreatorBroadcast,
  type CreatorEmailAudienceSummary,
  type CreatorEmailCampaign,
} from '../../../api/subscriptionEmail'

type TabKey = 'overview' | 'setup' | 'dashboard' | 'settings' | 'tiers' | 'email'

export function tabsForEnabled(enabled: boolean): Array<{ key: TabKey; label: string }> {
  return enabled
    ? [
        { key: 'overview', label: 'Overview' },
        { key: 'dashboard', label: 'Dashboard' },
        { key: 'settings', label: 'Settings' },
        { key: 'tiers', label: 'Tiers' },
        { key: 'email', label: 'Email' },
      ]
    : [
        { key: 'overview', label: 'Overview' },
        { key: 'setup', label: 'Setup' },
      ]
}

export function initialTabForEnabled(enabled: boolean): TabKey {
  return enabled ? 'dashboard' : 'overview'
}

export function parsePositiveInt(raw: string, opts?: { min?: number; max?: number }): number | null {
  const v = (raw || '').trim()
  if (!v) return null
  if (!/^\d+$/.test(v)) return null
  const n = Number(v)
  if (!Number.isFinite(n)) return null
  const min = opts?.min ?? 1
  const max = opts?.max ?? Number.POSITIVE_INFINITY
  if (n < min) return null
  if (n > max) return null
  return n
}

function Spinner({ className = '' }: { className?: string }) {
  return (
    <span
      className={[
        'inline-block h-4 w-4 rounded-full border border-white/20 border-t-white/80 animate-spin',
        className,
      ].join(' ')}
      aria-hidden="true"
    />
  )
}

function ErrorBanner({ message, onRetry, busy }: { message: string; onRetry: () => void; busy: boolean }) {
  return (
    <div className="rounded-xl border border-red-500/25 bg-red-500/10 p-3 flex items-start justify-between gap-3">
      <div>
        <div className="text-sm font-semibold text-red-200">Something went wrong</div>
        <div className="mt-1 text-xs text-red-100/80">{message}</div>
      </div>
      <button
        type="button"
        onClick={busy ? undefined : onRetry}
        disabled={busy}
        className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold transition border bg-red-500/15 text-red-100 border-red-500/30 hover:bg-red-500/25 hover:border-red-500/40 disabled:opacity-60"
      >
        {busy ? 'Retrying...' : 'Retry'}
      </button>
    </div>
  )
}

function TabBar({
  tabs,
  active,
  onChange,
}: {
  tabs: Array<{ key: TabKey; label: string }>
  active: TabKey
  onChange: (k: TabKey) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {tabs.map((t) => {
        const isActive = t.key === active
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            className={[
              'rounded-lg border px-3 py-1.5 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-sky-500/35 focus:ring-offset-0',
              isActive
                ? 'bg-white/10 border-white/15 text-white shadow-[0_0_0_1px_rgba(255,255,255,0.06)]'
                : 'bg-transparent border-transparent text-white/70 hover:bg-white/5 hover:border-white/10',
            ].join(' ')}
          >
            {t.label}
          </button>
        )
      })}
    </div>
  )
}

function PreviewSubscriberExperienceModal({
  open,
  onClose,
  allowPPV,
  ppvPricePixe,
  tiers,
}: {
  open: boolean
  onClose: () => void
  allowPPV: boolean
  ppvPricePixe: number | null
  tiers: CreatorTier[] | null
}) {
  if (!open) return null
  const activeTiers = (tiers || []).filter((t) => t.is_active).sort((a, b) => (a.rank || 0) - (b.rank || 0))
  const tierNames = activeTiers.map((t) => t.name).slice(0, 3)
  const hasTiers = tierNames.length > 0
  const ppvLabel = `Unlock for ${ppvPricePixe || 25} PIXE`
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center px-4">
      <button className="absolute inset-0 bg-black/60" onClick={onClose} aria-label="Close" />
      <div className="relative w-full max-w-2xl rounded-2xl border border-white/10 bg-neutral-950 p-5 shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-lg font-extrabold">Preview subscriber experience</div>
            <div className="mt-1 text-sm text-white/70">Teach the paywall model before you turn it on.</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm font-semibold hover:bg-white/10"
          >
            Close
          </button>
        </div>

        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold">Global feed (preview)</div>
              <SettingsChip>Locked</SettingsChip>
            </div>
            <div className="mt-3 rounded-xl border border-white/10 bg-black/30 p-4">
              <div className="text-sm font-semibold">New behind-the-scenes Pixe</div>
              <div className="mt-2 text-sm text-white/70 line-clamp-3">
                A short teaser appears here. The rest stays blurred until someone subscribes or unlocks.
              </div>
              <div className="mt-3 rounded-lg border border-white/10 bg-gradient-to-b from-white/10 to-transparent p-3 blur-[1px]">
                <div className="h-3 w-4/5 rounded bg-white/10" />
                <div className="mt-2 h-3 w-3/5 rounded bg-white/10" />
                <div className="mt-2 h-3 w-2/5 rounded bg-white/10" />
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="inline-flex items-center rounded-lg border border-sky-500/30 bg-sky-600/15 px-3 py-1.5 text-sm font-semibold text-sky-100">
                  Subscribe
                </span>
                {allowPPV ? (
                  <span className="inline-flex items-center rounded-lg border border-emerald-500/30 bg-emerald-600/10 px-3 py-1.5 text-sm font-semibold text-emerald-100">
                    {ppvLabel}
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold">Subscriptions mode (unlocked)</div>
              <SettingsChip variant="available">Unlocked</SettingsChip>
            </div>
            <div className="mt-3 rounded-xl border border-white/10 bg-black/30 p-4">
              <div className="text-sm font-semibold">New behind-the-scenes Pixe</div>
              <div className="mt-2 text-sm text-white/80">
                Full content renders here. Subscribers see subscriber-only Pixes unlocked in their feed lens.
              </div>
              <div className="mt-3 rounded-lg border border-white/10 bg-gradient-to-b from-white/10 to-transparent p-3">
                <div className="h-3 w-4/5 rounded bg-white/10" />
                <div className="mt-2 h-3 w-3/5 rounded bg-white/10" />
                <div className="mt-2 h-3 w-2/5 rounded bg-white/10" />
              </div>
              <div className="mt-4 text-xs text-white/60">Tip: toggle the feed lens on Home to view Subscriptions mode.</div>
            </div>
          </div>
        </div>

        <div className="mt-5 rounded-xl border border-white/10 bg-black/20 p-4">
          <div className="text-sm font-semibold">What this means</div>
          <ul className="mt-2 space-y-1 text-sm text-white/75 list-disc pl-5">
            <li>Subscribers auto-unlock subscriber-only Pixes.</li>
            <li>Pay-per-view unlocks are optional{allowPPV ? ' (enabled in your settings).' : '.'}</li>
            {hasTiers ? <li>Tiers are optional. Active tiers: {tierNames.join(', ')}.</li> : null}
            <li>Email notifications are opt-in for subscribers.</li>
          </ul>
        </div>

        <div className="mt-4 text-xs text-white/55">
          This is a static preview. Your real posts use access levels: Free, Subscribers-only, Pay-per-view, or Subscribers or PPV.
        </div>
      </div>
    </div>
  )
}

function DisableConfirmModal({
  open,
  onClose,
  onConfirm,
  busy,
}: {
  open: boolean
  onClose: () => void
  onConfirm: () => Promise<void>
  busy: boolean
}) {
  const [text, setText] = useState('')
  useEffect(() => {
    if (!open) setText('')
  }, [open])
  if (!open) return null
  const ok = text.trim() === 'DISABLE'
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center px-4">
      <button className="absolute inset-0 bg-black/60" onClick={onClose} aria-label="Close" />
      <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-neutral-950 p-5 shadow-xl">
        <div className="text-lg font-extrabold">Disable subscriptions</div>
        <div className="mt-1 text-sm text-white/75">
          This stops new subscriptions and renewals. Current subscribers keep access until the end of their current period.
        </div>
        <div className="mt-4">
          <div className="text-xs font-semibold text-white/70">Type DISABLE to confirm</div>
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="DISABLE"
            className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none focus:border-white/20"
          />
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold hover:bg-white/10"
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void onConfirm()}
            className="rounded-xl bg-red-500 px-4 py-2 text-sm font-semibold text-white hover:bg-red-600 disabled:opacity-60"
            disabled={busy || !ok}
          >
            {busy ? 'Working...' : 'Disable'}
          </button>
        </div>
      </div>
    </div>
  )
}

function FeatureToggles({
  value,
  onChange,
  disabled,
}: {
  value: Pick<
    CreatorSubscriptionSettings,
    | 'enable_subscriber_comments'
    | 'enable_early_access'
    | 'enable_subscriber_badge'
    | 'enable_polls'
    | 'enable_bts_pixies'
  >
  onChange: (patch: Partial<typeof value>) => void
  disabled?: boolean
}) {
  const items: Array<{ key: keyof typeof value; label: string; desc: string; comingSoon?: boolean }> = [
    { key: 'enable_subscriber_badge', label: 'Subscriber badge', desc: 'Show a badge next to subscribers in comments.', comingSoon: true },
    { key: 'enable_subscriber_comments', label: 'Subscriber comments', desc: 'Give subscribers an optional comments lane.', comingSoon: true },
    { key: 'enable_early_access', label: 'Early access', desc: 'Share Pixes with subscribers first.', comingSoon: true },
    { key: 'enable_polls', label: 'Polls', desc: 'Run lightweight polls for subscribers.', comingSoon: true },
    { key: 'enable_bts_pixies', label: 'Behind-the-scenes Pixes', desc: 'Organize BTS drops for members.', comingSoon: true },
  ]
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {items.map((it) => (
        <button
          key={String(it.key)}
          type="button"
          disabled={Boolean(disabled) || Boolean(it.comingSoon)}
          onClick={() => onChange({ [it.key]: !value[it.key] } as any)}
          className={[
            'rounded-xl border p-4 text-left transition',
            value[it.key] ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-white/10 bg-black/20',
            disabled || it.comingSoon ? 'opacity-60 cursor-not-allowed' : 'hover:bg-white/5 hover:border-white/15',
          ].join(' ')}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm font-semibold">{it.label}</div>
            {it.comingSoon ? (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/55">
                Coming soon
              </span>
            ) : value[it.key] ? (
              <SettingsChip variant="available">On</SettingsChip>
            ) : (
              <SettingsChip>Off</SettingsChip>
            )}
          </div>
          <div className="mt-1 text-xs text-white/65">{it.desc}</div>
        </button>
      ))}
    </div>
  )
}

export default function CreatorSubscriptions() {
  const authed = Boolean(getToken())

  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [settings, setSettings] = useState<CreatorSubscriptionSettings | null>(null)
  const [toast, setToast] = useState<{ visible: boolean; emoji: string; label: string; detail?: string; kind?: 'success' | 'error' }>({
    visible: false,
    emoji: 'OK',
    label: '',
    detail: undefined,
    kind: 'success',
  })

  const enabled = Boolean(settings?.is_enabled)

  const tabs = useMemo(() => tabsForEnabled(enabled), [enabled])
  const tabKeys = useMemo(() => new Set(tabs.map((t) => t.key)), [tabs])
  const [tab, setTab] = useState<TabKey>(() => initialTabForEnabled(false))
  const [didInitTab, setDidInitTab] = useState(false)

  const [previewOpen, setPreviewOpen] = useState(false)
  const [disableOpen, setDisableOpen] = useState(false)

  const [touched, setTouched] = useState(false)
  const [setupSubmitted, setSetupSubmitted] = useState(false)
  const [monthlyTouched, setMonthlyTouched] = useState(false)
  const [ppvTouched, setPPvTouched] = useState(false)
  const [settingsSubmitted, setSettingsSubmitted] = useState(false)
  const [settingsMonthlyTouched, setSettingsMonthlyTouched] = useState(false)
  const [settingsPPVTouched, setSettingsPPVTouched] = useState(false)
  const [monthlyPrice, setMonthlyPrice] = useState('')
  const [allowPPV, setAllowPPV] = useState(false)
  const [defaultPPV, setDefaultPPV] = useState('')
  const [pitchText, setPitchText] = useState('')
  const [features, setFeatures] = useState({
    enable_subscriber_comments: false,
    enable_early_access: false,
    enable_subscriber_badge: false,
    enable_polls: false,
    enable_bts_pixies: false,
  })

  const [tiers, setTiers] = useState<CreatorTier[] | null>(null)
  const [tiersLoading, setTiersLoading] = useState(false)
  const [tiersSaving, setTiersSaving] = useState(false)
  const [tiersError, setTiersError] = useState<string | null>(null)
  const [tierEdits, setTierEdits] = useState<Record<number, Partial<CreatorTier>>>({})
  const [newTierName, setNewTierName] = useState('')
  const [newTierRank, setNewTierRank] = useState<1 | 2 | 3>(1)
  const [newTierPrice, setNewTierPrice] = useState('')
  const [emailSummary, setEmailSummary] = useState<CreatorEmailAudienceSummary | null>(null)
  const [emailCampaigns, setEmailCampaigns] = useState<CreatorEmailCampaign[]>([])
  const [emailLoading, setEmailLoading] = useState(false)
  const [emailImportBusy, setEmailImportBusy] = useState(false)
  const [emailBroadcastBusy, setEmailBroadcastBusy] = useState(false)
  const [emailAddBusy, setEmailAddBusy] = useState(false)
  const [emailImportFile, setEmailImportFile] = useState<File | null>(null)
  const [emailConsent, setEmailConsent] = useState(false)
  const [emailManual, setEmailManual] = useState('')
  const [emailSubject, setEmailSubject] = useState('')
  const [emailBody, setEmailBody] = useState('')
  const [emailImportResult, setEmailImportResult] = useState<{
    summary: string
    firstInvalid: Array<{ row_number: number; raw_email: string; error: string }>
    firstDuplicates: Array<{ row_number: number; email: string }>
  } | null>(null)
  const [emailShowErrors, setEmailShowErrors] = useState(false)
  const [emailConfirmOpen, setEmailConfirmOpen] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)
  const emailFileInputRef = useRef<HTMLInputElement | null>(null)

  const monthlyPriceInt = parsePositiveInt(monthlyPrice, { min: 1, max: 9999 })
  const defaultPPVInt = parsePositiveInt(defaultPPV, { min: 1, max: 9999 })
  const newTierPriceInt = parsePositiveInt(newTierPrice, { min: 1, max: 999999 })

  const monthlyError =
    (setupSubmitted || monthlyTouched) && monthlyPrice.trim().length === 0
      ? 'Monthly price is required.'
      : (setupSubmitted || monthlyTouched) && monthlyPriceInt === null
        ? 'Enter a whole number (1-9999).'
        : null
  const ppvError =
    allowPPV && (setupSubmitted || ppvTouched) && defaultPPV.trim().length === 0
      ? 'Default PPV price is required when pay-per-view is on.'
      : allowPPV && (setupSubmitted || ppvTouched) && defaultPPVInt === null
        ? 'Enter a whole number (1-9999).'
        : null

  const canEnable = !saving && monthlyPriceInt !== null && (!allowPPV || defaultPPVInt !== null)
  const settingsDirty = Boolean(settings) && (
    monthlyPriceInt !== (settings?.monthly_price_pixe ?? null) ||
    allowPPV !== Boolean(settings?.allow_pay_per_view) ||
    (allowPPV ? defaultPPVInt : null) !== (settings?.default_ppv_price_pixe ?? null) ||
    (pitchText || '').trim() !== String((settings as any)?.pitch_text || '').trim() ||
    features.enable_subscriber_comments !== Boolean(settings?.enable_subscriber_comments) ||
    features.enable_early_access !== Boolean(settings?.enable_early_access) ||
    features.enable_subscriber_badge !== Boolean(settings?.enable_subscriber_badge) ||
    features.enable_polls !== Boolean(settings?.enable_polls) ||
    features.enable_bts_pixies !== Boolean(settings?.enable_bts_pixies)
  )
  const settingsValid = monthlyPriceInt !== null && (!allowPPV || defaultPPVInt !== null)
  const pitchTooLong = (pitchText || '').trim().length > 280
  const canSaveSettings = !saving && settingsDirty && settingsValid && !pitchTooLong

  const loadTiers = async () => {
    if (!authed || !enabled) return
    setTiersError(null)
    setTiersLoading(true)
    try {
      const res = await listMyCreatorTiers()
      setTiers(res?.results || [])
    } catch (e: any) {
      setTiersError(e?.response?.data?.detail || e?.message || 'Failed to load tiers')
      setTiers([])
    } finally {
      setTiersLoading(false)
    }
  }

  useEffect(() => {
    if (tab !== 'tiers' && tab !== 'settings') return
    if (tiers !== null) return
    void loadTiers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, enabled, authed])

  const loadEmail = async () => {
    if (!authed || !enabled) return
    setEmailError(null)
    setEmailLoading(true)
    try {
      const [summaryRes, campaignsRes] = await Promise.all([
        getCreatorEmailAudienceSummary(),
        listCreatorEmailCampaigns(),
      ])
      setEmailSummary(summaryRes)
      setEmailCampaigns(campaignsRes?.results || [])
    } catch (e: any) {
      setEmailError(e?.response?.data?.detail || e?.message || 'Failed to load email audience')
      setEmailSummary(null)
      setEmailCampaigns([])
    } finally {
      setEmailLoading(false)
    }
  }

  useEffect(() => {
    if (tab !== 'email') return
    if (!enabled) return
    if (emailSummary && emailCampaigns.length >= 0) return
    void loadEmail()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, enabled, authed])

  const setupTopRef = useRef<HTMLDivElement | null>(null)
  const monthlyPriceRef = useRef<HTMLInputElement | null>(null)
  const [focusSetupPrice, setFocusSetupPrice] = useState(false)
  const [flashPrice, setFlashPrice] = useState(false)

  const refetch = async (mode?: 'initial' | 'refresh') => {
    if (mode === 'refresh') setRefreshing(true)
    else setLoading(true)
    setError(null)
    try {
      const data = await getMyCreatorSubscriptionSettings()
      setSettings(data)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load subscription settings.')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    if (!authed) return
    void refetch('initial')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed])

  // Initialize form from server settings (but keep local edits when switching tabs).
  useEffect(() => {
    if (!settings) return
    if (touched) return
    setMonthlyPrice(settings.monthly_price_pixe ? String(settings.monthly_price_pixe) : '')
    setAllowPPV(Boolean(settings.allow_pay_per_view))
    setDefaultPPV(settings.default_ppv_price_pixe ? String(settings.default_ppv_price_pixe) : '')
    setPitchText(String((settings as any)?.pitch_text || ''))
    setSetupSubmitted(false)
    setMonthlyTouched(false)
    setPPvTouched(false)
    setSettingsSubmitted(false)
    setSettingsMonthlyTouched(false)
    setSettingsPPVTouched(false)
    setFeatures({
      enable_subscriber_comments: Boolean(settings.enable_subscriber_comments),
      enable_early_access: Boolean(settings.enable_early_access),
      enable_subscriber_badge: Boolean(settings.enable_subscriber_badge),
      enable_polls: Boolean(settings.enable_polls),
      enable_bts_pixies: Boolean(settings.enable_bts_pixies),
    })
  }, [settings, touched])

  // Keep tab valid when enabled state changes.
  useEffect(() => {
    if (!tabKeys.has(tab)) setTab(initialTabForEnabled(enabled))
  }, [enabled, tab, tabKeys])

  useEffect(() => {
    if (!settings) return
    if (didInitTab) return
    setTab(initialTabForEnabled(Boolean(settings.is_enabled)))
    setDidInitTab(true)
  }, [didInitTab, settings])

  useEffect(() => {
    if (tab !== 'setup') return
    if (!focusSetupPrice) return

    setupTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    requestAnimationFrame(() => {
      monthlyPriceRef.current?.focus()
      setFlashPrice(true)
      window.setTimeout(() => setFlashPrice(false), 900)
    })
    setFocusSetupPrice(false)
  }, [tab, focusSetupPrice])

  const statusChip = enabled ? <SettingsChip variant="available">Enabled</SettingsChip> : <SettingsChip>Not enabled</SettingsChip>

  const patch = async (p: Partial<CreatorSubscriptionSettings>, nextTab?: TabKey, toastLabel?: string) => {
    setSaving(true)
    setError(null)
    try {
      await patchMyCreatorSubscriptionSettings(p)
      await refetch('refresh')
      if (nextTab) setTab(nextTab)
      setTouched(false)
      if (toastLabel) setToast({ visible: true, emoji: 'OK', label: toastLabel, kind: 'success' })
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Unable to save settings.')
      setToast({ visible: true, emoji: '!', label: 'Save failed', detail: e?.response?.data?.detail || e?.message, kind: 'error' })
    } finally {
      setSaving(false)
    }
  }

  if (!authed) {
    return (
      <div className="space-y-4">
        <SettingsTitle title="Pixe Subscriptions" subtitle="Enable monthly support and subscriber-only Pixes." />
        <SettingsCard>
          <div className="text-sm text-white/75">Sign in to manage subscriptions.</div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link
              to="/login"
              className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold transition border bg-sky-600 text-white border-sky-500/40 hover:bg-sky-500 hover:border-sky-400/50"
            >
              Sign in
            </Link>
            <Link
              to="/signup"
              className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold transition border bg-sky-600/15 text-sky-100 border-sky-500/30 hover:bg-sky-600/25 hover:border-sky-400/40"
            >
              Create account
            </Link>
          </div>
        </SettingsCard>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <SettingsTitle
        title="Subscriptions"
        subtitle="Turn your biggest supporters into recurring members, and unlock subscriber-only Pixes in their feed."
        right={
          <button
            type="button"
            onClick={refreshing || loading || saving ? undefined : () => void refetch('refresh')}
            disabled={refreshing || loading || saving}
            className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold transition border bg-transparent text-white/85 border-transparent hover:bg-white/10 hover:border-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {refreshing ? (
              <span className="inline-flex items-center gap-2">
                <Spinner className="h-3.5 w-3.5" /> Refreshing
              </span>
            ) : (
              'Refresh'
            )}
          </button>
        }
      />

      <SettingsCard
        accent="key"
        title={enabled ? 'Subscriptions are live' : 'Turn subscriptions on'}
        subtitle={enabled ? 'Subscribers get an unlocked feed lens.' : 'Start with value, then enable when you are ready.'}
        right={
          <div className="flex items-center gap-2">
            {statusChip}
            <TabBar tabs={tabs} active={tab} onChange={setTab} />
          </div>
        }
      >
        {loading && !settings ? (
          <div className="animate-pulse space-y-4">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <div className="h-6 w-48 rounded bg-white/10" />
              <div className="mt-3 h-4 w-3/4 rounded bg-white/10" />
              <div className="mt-5 flex gap-2">
                <div className="h-9 w-28 rounded-lg bg-white/10" />
                <div className="h-9 w-48 rounded-lg bg-white/10" />
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              <div className="h-24 rounded-xl border border-white/10 bg-white/5" />
              <div className="h-24 rounded-xl border border-white/10 bg-white/5" />
              <div className="h-24 rounded-xl border border-white/10 bg-white/5" />
            </div>
          </div>
        ) : null}

        {error ? <ErrorBanner message={error} onRetry={() => void refetch('refresh')} busy={refreshing || loading || saving} /> : null}

        {tab === 'overview' ? (
          <div className="space-y-5">
            <div className="rounded-2xl border border-white/10 bg-[radial-gradient(1100px_500px_at_10%_0%,rgba(56,189,248,0.18),transparent_55%),radial-gradient(900px_500px_at_100%_15%,rgba(16,185,129,0.10),transparent_55%)] p-5">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="text-2xl font-extrabold">Subscriptions</div>
                  <div className="mt-1 text-sm text-white/75">Make membership feel native, not bolted on.</div>
                  <div className="mt-2 text-xs text-white/60">Change your price anytime. Disable anytime.</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <SettingsButton
                    onClick={() => {
                      if (enabled) setTab('dashboard')
                      else {
                        setTab('setup')
                        setFocusSetupPrice(true)
                      }
                    }}
                    disabled={loading || saving}
                  >
                    {enabled ? 'View Dashboard' : 'Get started'}
                  </SettingsButton>
                  <SettingsButton variant="secondary" onClick={() => setPreviewOpen(true)}>
                    Preview subscriber experience
                  </SettingsButton>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-white/65">
                <span className="rounded-full border border-white/10 bg-white/5 px-2 py-1">V1: PIXE payments</span>
                <span className="rounded-full border border-white/10 bg-white/5 px-2 py-1">Subscriber-only Pixes</span>
                <span className="rounded-full border border-white/10 bg-white/5 px-2 py-1">Optional PPV unlocks</span>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                <div className="text-sm font-semibold">Subscriber-only Pixes</div>
                <div className="mt-1 text-xs text-white/65">
                  Publish subscriber-only drops. Subscribers see them fully unlocked in Subscriptions mode.
                </div>
              </div>
              <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                <div className="text-sm font-semibold">Optional pay-per-view</div>
                <div className="mt-1 text-xs text-white/65">
                  Let non-subscribers unlock a single Pixe using PIXE, without changing your discovery feed.
                </div>
              </div>
              <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                <div className="text-sm font-semibold">Email notifications</div>
                <div className="mt-1 text-xs text-white/65">
                  When you publish, you can optionally queue a subscriber email. Notifications are opt-in.
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-black/20 p-4">
              <div className="text-sm font-semibold">How it works</div>
              <div className="mt-3 grid gap-3 md:grid-cols-3">
                <div className="rounded-xl border border-white/10 bg-black/10 p-4">
                  <div className="text-xs text-white/60">Step 1</div>
                  <div className="mt-1 font-semibold">Set your price</div>
                  <div className="mt-1 text-xs text-white/65">Pick a monthly PIXE price and (optionally) PPV.</div>
                </div>
                <div className="rounded-xl border border-white/10 bg-black/10 p-4">
                  <div className="text-xs text-white/60">Step 2</div>
                  <div className="mt-1 font-semibold">Publish access levels</div>
                  <div className="mt-1 text-xs text-white/65">Choose Free, Subscribers-only, PPV, or Subscribers or PPV.</div>
                </div>
                <div className="rounded-xl border border-white/10 bg-black/10 p-4">
                  <div className="text-xs text-white/60">Step 3</div>
                  <div className="mt-1 font-semibold">Subscribers see it unlocked</div>
                  <div className="mt-1 text-xs text-white/65">Your drops show up unlocked in Subscriptions mode.</div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-white/10 text-xs text-white/60">
                You can change your price later, and you can disable subscriptions any time.
              </div>
            </div>
          </div>
        ) : null}

        {tab === 'setup' ? (
          <div className="space-y-4">
            <div ref={setupTopRef} />
            <div className="text-sm text-white/75">Set your price, decide whether to offer pay-per-view unlocks, then enable.</div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-xl border border-white/10 bg-black/20 p-4 space-y-3">
                <div>
                  <div className="text-xs text-white/60">Step 1</div>
                  <div className="text-sm font-semibold">Set your monthly price</div>
                  <div className="mt-2 text-xs font-semibold text-white/70">Monthly price (PIXE)</div>
                  <input
                    ref={monthlyPriceRef}
                    value={monthlyPrice}
                    onChange={(e) => {
                      setTouched(true)
                      setMonthlyTouched(true)
                      setMonthlyPrice(e.target.value)
                    }}
                    onBlur={() => setMonthlyTouched(true)}
                    placeholder="e.g. 250"
                    className={[
                      'mt-2 w-full rounded-xl border bg-white/5 px-3 py-2 text-sm text-white outline-none',
                      monthlyError ? 'border-red-500/30 focus:border-red-500/40' : 'border-white/10 focus:border-white/20',
                      flashPrice ? 'ring-2 ring-sky-400/60 shadow-[0_0_0_6px_rgba(56,189,248,0.12)]' : '',
                    ].join(' ')}
                  />
                  <div className="mt-2 text-xs text-white/60">Most creators start at 5-15 PIXE/month.</div>
                  {monthlyError ? <div className="mt-2 text-xs text-red-300">{monthlyError}</div> : null}
                </div>

                <div className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 p-3">
                  <div>
                    <div className="text-xs text-white/60">Step 2</div>
                    <div className="text-sm font-semibold">Optional pay-per-view unlock</div>
                    <div className="text-xs text-white/60">Non-subscribers can unlock a single Pixe.</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setTouched(true)
                      setPPvTouched(false)
                      setAllowPPV((v) => !v)
                    }}
                    className={[
                      'h-8 w-14 rounded-full border transition relative',
                      allowPPV ? 'bg-emerald-500/20 border-emerald-500/30' : 'bg-white/5 border-white/10',
                    ].join(' ')}
                    aria-label="Toggle pay per view"
                  >
                    <span
                      className={['absolute top-1 h-6 w-6 rounded-full bg-white transition', allowPPV ? 'left-7' : 'left-1'].join(' ')}
                    />
                  </button>
                </div>

                {allowPPV ? (
                  <div>
                    <div className="text-xs font-semibold text-white/70">Default PPV price (PIXE)</div>
                    <input
                      value={defaultPPV}
                      onChange={(e) => {
                        setTouched(true)
                        setPPvTouched(true)
                        setDefaultPPV(e.target.value)
                      }}
                      onBlur={() => setPPvTouched(true)}
                      placeholder="e.g. 25"
                      className={[
                        'mt-2 w-full rounded-xl border bg-white/5 px-3 py-2 text-sm text-white outline-none',
                        ppvError ? 'border-red-500/30 focus:border-red-500/40' : 'border-white/10 focus:border-white/20',
                      ].join(' ')}
                    />
                    <div className="mt-2 text-xs text-white/60">Shown on locked Pixes as “Unlock for X PIXE”.</div>
                    {ppvError ? <div className="mt-2 text-xs text-red-300">{ppvError}</div> : null}
                  </div>
                ) : null}
              </div>

              <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                <div className="text-sm font-semibold">Step 3</div>
                <div className="mt-1 text-sm text-white/80">
                  When enabled, subscribers will see your subscriber-only Pixes unlocked in Subscriptions mode.
                </div>
                <ul className="mt-3 space-y-2 text-sm text-white/75">
                  <li>Subscribers will see subscriber-only Pixes fully unlocked.</li>
                  <li>Non-subscribers will see previews and an unlock CTA.</li>
                  <li>V1 uses PIXE payments.</li>
                  <li>Billing is in 30-day periods. Renewals are manual for now.</li>
                </ul>
                <div className="mt-4 flex flex-wrap gap-2">
                  <SettingsButton
                    onClick={() => {
                      setSetupSubmitted(true)
                      if (!canEnable) return
                      void patch(
                        {
                          is_enabled: true,
                          currency: 'PIXE',
                          monthly_price_pixe: monthlyPriceInt,
                          allow_pay_per_view: allowPPV,
                          default_ppv_price_pixe: allowPPV ? defaultPPVInt : null,
                          pitch_text: (pitchText || '').trim(),
                          ...features,
                        },
                        'dashboard',
                        'Subscriptions enabled.'
                      )
                    }}
                    disabled={!canEnable}
                  >
                    {saving ? 'Enabling...' : 'Enable subscriptions'}
                  </SettingsButton>
                  <SettingsButton variant="secondary" onClick={() => setPreviewOpen(true)}>
                    Preview subscriber experience
                  </SettingsButton>
                </div>
                <div className="mt-3 text-xs text-white/60">You can change your price later, and you can disable anytime.</div>
              </div>
            </div>
          </div>
        ) : null}

        {tab === 'dashboard' ? (
          <div className="space-y-4">
            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                <div className="text-xs text-white/60">Subscribers</div>
                <div className="mt-1 text-2xl font-extrabold">—</div>
                <div className="mt-1 text-xs text-white/55">Appears after your first subscriber.</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                <div className="text-xs text-white/60">Monthly revenue (PIXE)</div>
                <div className="mt-1 text-2xl font-extrabold">—</div>
                <div className="mt-1 text-xs text-white/55">Appears after your first subscriber.</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                <div className="text-xs text-white/60">PPV unlocks (30d)</div>
                <div className="mt-1 text-2xl font-extrabold">—</div>
                <div className="mt-1 text-xs text-white/55">Appears after your first unlock.</div>
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-black/20 p-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="text-sm font-semibold">Quick actions</div>
                  <div className="mt-1 text-xs text-white/65">
                    Keep it simple: publish a subscriber-only Pixe, then preview your subscriber lens.
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Link
                    to="/publish/new"
                    className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold transition border bg-sky-600 text-white border-sky-500/40 hover:bg-sky-500 hover:border-sky-400/50"
                  >
                    Create subscriber-only Pixe
                  </Link>
                  <SettingsButton variant="secondary" onClick={() => setPreviewOpen(true)}>
                    Preview as subscriber
                  </SettingsButton>
                  <SettingsButton variant="ghost" onClick={() => setTab('settings')}>
                    Manage settings
                  </SettingsButton>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-black/20 p-4">
              <div className="text-sm font-semibold">Current pricing</div>
              <div className="mt-3 flex items-center justify-between text-sm">
                <span className="text-white/65">Monthly</span>
                <span className="font-semibold">{settings?.monthly_price_pixe ? `${settings.monthly_price_pixe} PIXE` : 'Not set'}</span>
              </div>
              <div className="mt-2 flex items-center justify-between text-sm">
                <span className="text-white/65">Pay-per-view</span>
                <span className="font-semibold">
                  {settings?.allow_pay_per_view ? `${settings.default_ppv_price_pixe || 'Custom'} PIXE` : 'Off'}
                </span>
              </div>
            </div>
          </div>
        ) : null}

        {tab === 'tiers' ? (
          <div className="space-y-4">
            <div className="rounded-xl border border-white/10 bg-black/20 p-4">
              <div className="text-sm font-semibold">Tiers</div>
              <div className="mt-1 text-sm text-white/70">
                Keep tiers simple. Higher tiers include lower-tier access. Max 3 active tiers.
              </div>
              <div className="mt-3 text-xs text-white/60">
                Tip: Tier gating is optional. If you don’t set a tier requirement on a Pixe, any subscriber can read it.
              </div>
            </div>

            {tiersError ? (
              <ErrorBanner message={tiersError} onRetry={() => void loadTiers()} busy={tiersLoading} />
            ) : null}

            <div className="rounded-xl border border-white/10 bg-black/20 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-semibold">Your tiers</div>
                <button
                  type="button"
                  onClick={() => void loadTiers()}
                  disabled={tiersLoading}
                  className="rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/90 hover:bg-white/10 disabled:opacity-60"
                >
                  {tiersLoading ? 'Refreshing…' : 'Refresh'}
                </button>
              </div>

              {tiersLoading && tiers === null ? (
                <div className="mt-4 grid gap-2">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="h-16 rounded-xl border border-white/10 bg-white/5 animate-pulse" />
                  ))}
                </div>
              ) : (
                <div className="mt-4 grid gap-2">
                  {(tiers || []).length === 0 ? (
                    <div className="text-sm text-white/60">No tiers yet. Add up to 3 tiers below.</div>
                  ) : (
                    (tiers || []).map((t) => {
                      const edit = tierEdits[t.id] || {}
                      const name = (edit.name ?? t.name) as string
                      const rank = (edit.rank ?? t.rank) as number
                      const price = (edit.monthly_price_pixe ?? t.monthly_price_pixe) as number
                      const isActive = Boolean(edit.is_active ?? t.is_active)
                      return (
                        <div key={t.id} className="rounded-xl border border-white/10 bg-white/5 p-3">
                          <div className="grid gap-3 md:grid-cols-12 md:items-end">
                            <label className="md:col-span-4 block">
                              <div className="text-xs text-white/60 mb-1">Name</div>
                              <input
                                value={name}
                                onChange={(e) => setTierEdits((p) => ({ ...p, [t.id]: { ...p[t.id], name: e.target.value } }))}
                                className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:border-white/20"
                              />
                            </label>
                            <label className="md:col-span-2 block">
                              <div className="text-xs text-white/60 mb-1">Rank</div>
                              <select
                                value={String(rank)}
                                onChange={(e) => setTierEdits((p) => ({ ...p, [t.id]: { ...p[t.id], rank: Number(e.target.value) } }))}
                                className="w-full rounded-lg bg-neutral-950 text-white border border-white/10 px-3 py-2 text-sm outline-none focus:border-white/20"
                                style={{ colorScheme: 'dark' } as any}
                              >
                                <option value="1">1</option>
                                <option value="2">2</option>
                                <option value="3">3</option>
                              </select>
                            </label>
                            <label className="md:col-span-3 block">
                              <div className="text-xs text-white/60 mb-1">Monthly (PIXE)</div>
                              <input
                                value={String(price)}
                                onChange={(e) => setTierEdits((p) => ({ ...p, [t.id]: { ...p[t.id], monthly_price_pixe: Number(e.target.value) } }))}
                                className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:border-white/20"
                                inputMode="numeric"
                              />
                            </label>
                            <div className="md:col-span-3 flex items-center justify-between gap-2">
                              <button
                                type="button"
                                onClick={() => setTierEdits((p) => ({ ...p, [t.id]: { ...p[t.id], is_active: !isActive } }))}
                                className={[
                                  'rounded-lg border px-3 py-2 text-xs font-semibold',
                                  isActive ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-100' : 'border-white/10 bg-white/5 text-white/70',
                                ].join(' ')}
                              >
                                {isActive ? 'Active' : 'Inactive'}
                              </button>
                              <button
                                type="button"
                                disabled={tiersSaving}
                                onClick={async () => {
                                  setTiersError(null)
                                  setTiersSaving(true)
                                  try {
                                    await patchMyCreatorTier(t.id, {
                                      name: name.trim(),
                                      rank: rank as any,
                                      monthly_price_pixe: price as any,
                                      is_active: isActive,
                                    })
                                    setTierEdits((p) => {
                                      const next = { ...p }
                                      delete next[t.id]
                                      return next
                                    })
                                    await loadTiers()
                                  } catch (e: any) {
                                    setTiersError(e?.response?.data?.detail || e?.message || 'Failed to save tier')
                                  } finally {
                                    setTiersSaving(false)
                                  }
                                }}
                                className="rounded-lg bg-brand px-3 py-2 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                disabled={tiersSaving}
                                onClick={async () => {
                                  if (!window.confirm('Delete this tier? Existing subscriptions will keep their current tier until changed.')) return
                                  setTiersError(null)
                                  setTiersSaving(true)
                                  try {
                                    await deleteMyCreatorTier(t.id)
                                    await loadTiers()
                                  } catch (e: any) {
                                    setTiersError(e?.response?.data?.detail || e?.message || 'Failed to delete tier')
                                  } finally {
                                    setTiersSaving(false)
                                  }
                                }}
                                className="rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-xs font-semibold text-red-100 hover:bg-red-500/15 disabled:opacity-60"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-white/10 bg-black/20 p-4">
              <div className="text-sm font-semibold">Add a tier</div>
              <div className="mt-3 grid gap-3 md:grid-cols-12 md:items-end">
                <label className="md:col-span-5 block">
                  <div className="text-xs text-white/60 mb-1">Name</div>
                  <input
                    value={newTierName}
                    onChange={(e) => setNewTierName(e.target.value)}
                    placeholder="Supporter"
                    className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:border-white/20"
                  />
                </label>
                <label className="md:col-span-2 block">
                  <div className="text-xs text-white/60 mb-1">Rank</div>
                  <select
                    value={String(newTierRank)}
                    onChange={(e) => setNewTierRank(Number(e.target.value) as any)}
                    className="w-full rounded-lg bg-neutral-950 text-white border border-white/10 px-3 py-2 text-sm outline-none focus:border-white/20"
                    style={{ colorScheme: 'dark' } as any}
                  >
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                  </select>
                </label>
                <label className="md:col-span-3 block">
                  <div className="text-xs text-white/60 mb-1">Monthly (PIXE)</div>
                  <input
                    value={newTierPrice}
                    onChange={(e) => setNewTierPrice(e.target.value)}
                    placeholder="10"
                    className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:border-white/20"
                    inputMode="numeric"
                  />
                </label>
                <div className="md:col-span-2">
                  <button
                    type="button"
                    disabled={
                      tiersSaving ||
                      !newTierName.trim() ||
                      newTierPriceInt === null ||
                      ((tiers || []).filter((t) => t.is_active).length >= 3)
                    }
                    onClick={async () => {
                      setTiersError(null)
                      setTiersSaving(true)
                      try {
                        await createMyCreatorTier({
                          name: newTierName.trim(),
                          rank: newTierRank,
                          monthly_price_pixe: newTierPriceInt as number,
                          is_active: true,
                        })
                        setNewTierName('')
                        setNewTierPrice('')
                        await loadTiers()
                      } catch (e: any) {
                        setTiersError(e?.response?.data?.detail || e?.message || 'Failed to create tier')
                      } finally {
                        setTiersSaving(false)
                      }
                    }}
                    className="w-full rounded-lg bg-brand px-3 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
                  >
                    Add
                  </button>
                </div>
              </div>
              {(tiers || []).filter((t) => t.is_active).length >= 3 ? (
                <div className="mt-2 text-xs text-amber-200/80">You already have 3 active tiers. Disable one to add another.</div>
              ) : null}
            </div>
          </div>
        ) : null}

        {tab === 'settings' ? (
          <div className="space-y-4">
            <div className="rounded-xl border border-white/10 bg-black/20 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold">Live pricing</div>
                  <div className="mt-1 text-xs text-white/60">This is what subscribers see right now.</div>
                </div>
                <div className="text-xs text-white/60">
                  Billing: 30-day periods. Renewals are manual for now.
                </div>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                  <div className="text-xs text-white/60">Monthly</div>
                  <div className="mt-1 text-sm font-semibold">
                    {settings?.monthly_price_pixe ? `${settings.monthly_price_pixe} PIXE` : '—'}
                  </div>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                  <div className="text-xs text-white/60">Pay-per-view</div>
                  <div className="mt-1 text-sm font-semibold">
                    {settings?.allow_pay_per_view ? `${settings.default_ppv_price_pixe || 'Custom'} PIXE` : 'Off'}
                  </div>
                  {settings?.allow_pay_per_view ? (
                    <div className="mt-1 text-xs text-white/55">Used by default (you can override per Pixe).</div>
                  ) : null}
                </div>
                <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                  <div className="text-xs text-white/60">Active tiers</div>
                  <div className="mt-1 text-sm font-semibold">
                    {tiers === null ? '—' : String((tiers || []).filter((t) => t.is_active).length)}
                  </div>
                  <div className="mt-1 text-xs text-white/55">Optional. Higher tiers include lower tiers.</div>
                </div>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-xl border border-white/10 bg-black/20 p-4 space-y-3">
                <div>
                  <div className="text-xs font-semibold text-white/70">Monthly price (PIXE)</div>
                  <input
                    value={monthlyPrice}
                    onChange={(e) => {
                      setTouched(true)
                      setSettingsMonthlyTouched(true)
                      setMonthlyPrice(e.target.value)
                    }}
                    onBlur={() => setSettingsMonthlyTouched(true)}
                    className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none focus:border-white/20"
                  />
                  {(settingsSubmitted || settingsMonthlyTouched) && monthlyPriceInt === null ? (
                    <div className="mt-2 text-xs text-red-300">Enter a whole number (1-9999).</div>
                  ) : (
                    <div className="mt-2 text-xs text-white/60">Most creators start at 5-15 PIXE/month.</div>
                  )}
                </div>

                <div className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 p-3">
                  <div>
                    <div className="text-sm font-semibold">Allow pay-per-view</div>
                    <div className="text-xs text-white/60">Let non-subscribers unlock a single Pixe.</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setTouched(true)
                      setSettingsPPVTouched(false)
                      setAllowPPV((v) => !v)
                    }}
                    className={[
                      'h-8 w-14 rounded-full border transition relative',
                      allowPPV ? 'bg-emerald-500/20 border-emerald-500/30' : 'bg-white/5 border-white/10',
                    ].join(' ')}
                    aria-label="Toggle pay per view"
                  >
                    <span
                      className={['absolute top-1 h-6 w-6 rounded-full bg-white transition', allowPPV ? 'left-7' : 'left-1'].join(' ')}
                    />
                  </button>
                </div>

                {allowPPV ? (
                  <div>
                    <div className="text-xs font-semibold text-white/70">Default PPV price (PIXE)</div>
                    <input
                      value={defaultPPV}
                      onChange={(e) => {
                        setTouched(true)
                        setSettingsPPVTouched(true)
                        setDefaultPPV(e.target.value)
                      }}
                      onBlur={() => setSettingsPPVTouched(true)}
                      className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none focus:border-white/20"
                    />
                    {(settingsSubmitted || settingsPPVTouched) && defaultPPV.trim().length === 0 ? (
                      <div className="mt-2 text-xs text-red-300">Required when pay-per-view is on.</div>
                    ) : (settingsSubmitted || settingsPPVTouched) && defaultPPVInt === null ? (
                      <div className="mt-2 text-xs text-red-300">Enter a whole number of PIXE (at least 1).</div>
                    ) : (
                      <div className="mt-2 text-xs text-white/60">Non-subscribers will see “{`Unlock for ${defaultPPVInt || 25} PIXE` }”.</div>
                    )}
                    <div className="mt-2 text-xs text-white/55">You can override the PPV price per Pixe when publishing.</div>
                  </div>
                ) : null}

                <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                  <div className="text-sm font-semibold">Profile pitch</div>
                  <div className="mt-1 text-xs text-white/60">Shown on your profile to help visitors understand why they should subscribe.</div>
                  <textarea
                    value={pitchText}
                    onChange={(e) => { setTouched(true); setPitchText(e.target.value) }}
                    rows={3}
                    maxLength={600}
                    placeholder="Get premium Pixes, behind-the-scenes drops, and subscriber-only posts."
                    className={[
                      'mt-3 w-full rounded-xl border bg-black/30 px-3 py-2 text-sm text-white outline-none focus:border-white/20',
                      pitchTooLong ? 'border-red-500/40' : 'border-white/10',
                    ].join(' ')}
                  />
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <div className={pitchTooLong ? 'text-red-300' : 'text-white/55'}>
                      {pitchTooLong ? 'Keep it under 280 characters.' : 'Keep it short: 1-2 sentences.'}
                    </div>
                    <div className={pitchTooLong ? 'text-red-300' : 'text-white/55'}>
                      {(pitchText || '').trim().length}/280
                    </div>
                  </div>
                </div>

                <div className="pt-2 flex flex-wrap gap-2">
                  <SettingsButton
                    onClick={() => {
                      setSettingsSubmitted(true)
                      if (!canSaveSettings) return
                      void patch(
                        {
                          monthly_price_pixe: monthlyPriceInt,
                          allow_pay_per_view: allowPPV,
                          default_ppv_price_pixe: allowPPV ? defaultPPVInt : null,
                          pitch_text: (pitchText || '').trim(),
                          ...features,
                        },
                        undefined,
                        'Saved.'
                      )
                    }}
                    disabled={!canSaveSettings}
                  >
                    {saving ? 'Saving...' : 'Save changes'}
                  </SettingsButton>
                  <SettingsButton variant="secondary" onClick={() => setPreviewOpen(true)}>
                    Preview
                  </SettingsButton>
                  {!saving && !settingsDirty ? <div className="flex items-center text-xs text-white/55">No changes to save.</div> : null}
                </div>
              </div>

              <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                <MembershipFeaturesPanel
                  value={features}
                  onChange={(p) => {
                    setTouched(true)
                    setFeatures((v) => ({ ...v, ...p }))
                  }}
                />
              </div>
            </div>

            <div className="rounded-xl border border-red-500/25 bg-red-500/10 p-4">
              <div className="text-sm font-semibold text-red-200">Danger zone</div>
              <div className="mt-1 text-xs text-red-100/80">
                Disable subscriptions to stop new signups and renewals. Current subscribers keep access until the end of their current period.
              </div>
              <div className="mt-4">
                <SettingsButton variant="danger" onClick={() => setDisableOpen(true)} disabled={saving}>
                  Disable subscriptions
                </SettingsButton>
              </div>
            </div>
          </div>
        ) : null}

        {tab === 'email' ? (
          <div className="space-y-4">
            {!enabled ? (
              <div className="rounded-xl border border-white/10 bg-black/20 p-4 text-sm text-white/70">
                Enable subscriptions first to use Email Audience.
              </div>
            ) : null}

            {enabled && emailError ? (
              <ErrorBanner message={emailError} onRetry={() => void loadEmail()} busy={emailLoading} />
            ) : null}

            {enabled ? (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                  <div className="text-xs text-white/60">Total</div>
                  <div className="mt-1 text-2xl font-extrabold">{emailSummary ? emailSummary.total : '—'}</div>
                </div>
                <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                  <div className="text-xs text-white/60">Subscribed</div>
                  <div className="mt-1 text-2xl font-extrabold">{emailSummary ? emailSummary.subscribed : '—'}</div>
                </div>
                <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                  <div className="text-xs text-white/60">Unsubscribed</div>
                  <div className="mt-1 text-2xl font-extrabold">{emailSummary ? emailSummary.unsubscribed : '—'}</div>
                </div>
                <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                  <div className="text-xs text-white/60">Bounced / Complained</div>
                  <div className="mt-1 text-2xl font-extrabold">
                    {emailSummary ? `${emailSummary.bounced} / ${emailSummary.complained}` : '—'}
                  </div>
                  <div className="mt-1 text-xs text-white/55">
                    Last import: {emailSummary?.last_import_at ? new Date(emailSummary.last_import_at).toLocaleString() : '—'}
                  </div>
                </div>
              </div>
            ) : null}

            {enabled && emailSummary?.sending_blocked ? (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-100">
                Sending paused: {emailSummary.sending_block_reason || 'Bounce/complaint threshold reached.'}
              </div>
            ) : null}

            {enabled ? (
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-xl border border-white/10 bg-black/20 p-4 space-y-3">
                  <div className="text-sm font-semibold">Import CSV</div>
                  <input
                    ref={emailFileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(e) => setEmailImportFile(e.target.files?.[0] || null)}
                    className="hidden"
                  />
                  <div className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => emailFileInputRef.current?.click()}
                        className="rounded-md border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15"
                      >
                        Choose CSV
                      </button>
                      <div className="min-w-0 flex-1 text-xs text-white/80 truncate">
                        {emailImportFile ? emailImportFile.name : 'No file selected'}
                      </div>
                      {emailImportFile ? (
                        <button
                          type="button"
                          onClick={() => {
                            setEmailImportFile(null)
                            if (emailFileInputRef.current) emailFileInputRef.current.value = ''
                          }}
                          className="rounded-md border border-white/15 bg-white/5 px-2 py-1 text-[11px] font-semibold text-white/80 hover:bg-white/10"
                        >
                          Clear
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <div className="text-[11px] text-white/55">CSV only. Use headers like email or Email Address.</div>
                  <label className="flex items-start gap-2 text-xs text-white/75">
                    <input
                      type="checkbox"
                      checked={emailConsent}
                      onChange={(e) => setEmailConsent(e.target.checked)}
                      className="mt-0.5"
                    />
                    <span>I confirm these contacts opted in to receive my emails.</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <SettingsButton
                      disabled={!emailImportFile || !emailConsent || emailImportBusy}
                      onClick={async () => {
                        if (!emailImportFile) return
                        setEmailImportBusy(true)
                        setEmailError(null)
                        setEmailImportResult(null)
                        try {
                          const res = await importCreatorEmailAudience(emailImportFile, emailConsent)
                          setEmailImportResult({
                            summary: `Imported ${res.imported_count}, skipped ${res.duplicate_count} duplicates, ${res.invalid_count} invalid.`,
                            firstInvalid: res.first_invalid || [],
                            firstDuplicates: res.first_duplicates || [],
                          })
                          setEmailShowErrors(false)
                          setEmailImportFile(null)
                          if (emailFileInputRef.current) emailFileInputRef.current.value = ''
                          setEmailConsent(false)
                          await loadEmail()
                          setToast({ visible: true, emoji: 'OK', label: 'Import complete', kind: 'success' })
                        } catch (e: any) {
                          setEmailError(e?.response?.data?.detail || e?.message || 'Import failed')
                          setToast({ visible: true, emoji: '!', label: 'Import failed', detail: e?.response?.data?.detail || e?.message, kind: 'error' })
                        } finally {
                          setEmailImportBusy(false)
                        }
                      }}
                    >
                      {emailImportBusy ? 'Importing...' : 'Import'}
                    </SettingsButton>
                    <SettingsButton
                      variant="secondary"
                      disabled={emailLoading}
                      onClick={async () => {
                        try {
                          const blob = await exportCreatorEmailAudienceCsv()
                          const url = URL.createObjectURL(blob)
                          const a = document.createElement('a')
                          a.href = url
                          a.download = 'pixeloop_email_audience.csv'
                          document.body.appendChild(a)
                          a.click()
                          a.remove()
                          URL.revokeObjectURL(url)
                        } catch (e: any) {
                          setEmailError(e?.response?.data?.detail || e?.message || 'Export failed')
                        }
                      }}
                    >
                      Export CSV
                    </SettingsButton>
                  </div>
                  {emailImportResult ? (
                    <div className="rounded-lg border border-white/10 bg-white/5 p-3 text-xs text-white/75">
                      <div>{emailImportResult.summary}</div>
                      {(emailImportResult.firstInvalid?.length || emailImportResult.firstDuplicates?.length) ? (
                        <div className="mt-2">
                          <button
                            type="button"
                            onClick={() => setEmailShowErrors((v) => !v)}
                            className="rounded border border-white/10 bg-white/5 px-2 py-1 text-[11px] font-semibold hover:bg-white/10"
                          >
                            {emailShowErrors ? 'Hide details' : 'View details'}
                          </button>
                          {emailShowErrors ? (
                            <div className="mt-2 space-y-2">
                              {emailImportResult.firstInvalid?.length ? (
                                <div>
                                  <div className="text-[11px] font-semibold text-red-200">Invalid rows (first 10)</div>
                                  <ul className="mt-1 list-disc pl-5 space-y-1">
                                    {emailImportResult.firstInvalid.map((x, idx) => (
                                      <li key={`inv-${idx}`}>
                                        row {x.row_number}: {x.raw_email || '(empty)'} - {x.error}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ) : null}
                              {emailImportResult.firstDuplicates?.length ? (
                                <div>
                                  <div className="text-[11px] font-semibold text-amber-100">Duplicates (first 10)</div>
                                  <ul className="mt-1 list-disc pl-5 space-y-1">
                                    {emailImportResult.firstDuplicates.map((x, idx) => (
                                      <li key={`dup-${idx}`}>
                                        row {x.row_number}: {x.email}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                <div className="rounded-xl border border-white/10 bg-black/20 p-4 space-y-3">
                  <div className="text-sm font-semibold">Add email manually</div>
                  <div className="flex gap-2">
                    <input
                      value={emailManual}
                      onChange={(e) => setEmailManual(e.target.value)}
                      type="email"
                      placeholder="name@example.com"
                      className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:border-white/20"
                    />
                    <SettingsButton
                      disabled={!emailManual.trim() || emailAddBusy}
                      onClick={async () => {
                        setEmailAddBusy(true)
                        setEmailError(null)
                        try {
                          await addCreatorEmailContact(emailManual.trim())
                          setEmailManual('')
                          await loadEmail()
                          setToast({ visible: true, emoji: 'OK', label: 'Contact added', kind: 'success' })
                        } catch (e: any) {
                          setEmailError(e?.response?.data?.detail || e?.message || 'Failed to add contact')
                        } finally {
                          setEmailAddBusy(false)
                        }
                      }}
                    >
                      Add
                    </SettingsButton>
                  </div>

                  <div className="pt-2 border-t border-white/10">
                    <div className="text-sm font-semibold">Send broadcast</div>
                    <div className="mt-1 text-xs text-white/60">
                      Sends to your Email Audience: imported + manual + opted-in subscribers.
                    </div>
                    <div className="mt-1 text-xs text-white/55">
                      Includes unsubscribe link. Keep it relevant.
                    </div>
                    <input
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      placeholder="Subject"
                      className="mt-2 w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:border-white/20"
                    />
                    <textarea
                      value={emailBody}
                      onChange={(e) => setEmailBody(e.target.value)}
                      placeholder="Short message (optional)"
                      rows={4}
                      className="mt-2 w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:border-white/20"
                    />
                    <div className="mt-2">
                      <SettingsButton
                        disabled={!emailSubject.trim() || emailBroadcastBusy || Boolean(emailSummary?.sending_blocked) || Number(emailSummary?.broadcast_recipient_count ?? emailSummary?.subscribed ?? 0) <= 0}
                        onClick={() => setEmailConfirmOpen(true)}
                      >
                        {emailBroadcastBusy ? 'Queueing...' : 'Send'}
                      </SettingsButton>
                      <div className="mt-2 text-xs text-white/55">
                        Recipients: {Number(emailSummary?.broadcast_recipient_count ?? emailSummary?.subscribed ?? 0)}
                      </div>
                      {Number(emailSummary?.broadcast_recipient_count ?? emailSummary?.subscribed ?? 0) <= 0 ? (
                        <div className="mt-2 text-xs text-white/55">No subscribed recipients yet.</div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {enabled ? (
              <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm font-semibold">Recent campaigns</div>
                  <button
                    type="button"
                    disabled={emailLoading}
                    onClick={() => void loadEmail()}
                    className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold hover:bg-white/10 disabled:opacity-60"
                  >
                    {emailLoading ? 'Refreshing…' : 'Refresh'}
                  </button>
                </div>
                {(emailCampaigns || []).length === 0 ? (
                  <div className="mt-3 text-sm text-white/60">No campaigns yet.</div>
                ) : (
                  <div className="mt-3 overflow-x-auto">
                    <table className="min-w-full text-left text-sm">
                      <thead className="text-white/60">
                        <tr>
                          <th className="py-2 pr-3">Subject</th>
                          <th className="py-2 pr-3">Type</th>
                          <th className="py-2 pr-3">Status</th>
                          <th className="py-2 pr-3">Sent</th>
                          <th className="py-2 pr-3">Attempted</th>
                          <th className="py-2 pr-3">Delivered</th>
                          <th className="py-2 pr-3">Bounced</th>
                          <th className="py-2 pr-3">Complaints</th>
                          <th className="py-2 pr-3">Unsubs</th>
                        </tr>
                      </thead>
                      <tbody>
                        {emailCampaigns.map((c) => (
                          <tr key={c.id} className="border-t border-white/10">
                            <td className="py-2 pr-3">{c.subject || '(no subject)'}</td>
                            <td className="py-2 pr-3">{c.type}</td>
                            <td className="py-2 pr-3">{c.status}</td>
                            <td className="py-2 pr-3">{c.sent_at ? new Date(c.sent_at).toLocaleString() : '—'}</td>
                            <td className="py-2 pr-3">{c.attempted || 0}</td>
                            <td className="py-2 pr-3">{c.delivered || 0}</td>
                            <td className="py-2 pr-3">{c.bounced || 0}</td>
                            <td className="py-2 pr-3">{c.complained || 0}</td>
                            <td className="py-2 pr-3">{c.unsubscribed || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        ) : null}
      </SettingsCard>

      {emailConfirmOpen ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center px-4">
          <button className="absolute inset-0 bg-black/60" onClick={() => setEmailConfirmOpen(false)} aria-label="Close" />
          <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-neutral-950 p-5 shadow-xl">
            <div className="text-lg font-extrabold">Send broadcast</div>
            <div className="mt-1 text-sm text-white/75">
              Send to {Number(emailSummary?.broadcast_recipient_count ?? emailSummary?.subscribed ?? 0)} recipients?
            </div>
            <div className="mt-2 text-xs text-white/60">Includes unsubscribe link and one-click unsubscribe headers.</div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setEmailConfirmOpen(false)}
                className="rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold hover:bg-white/10"
                disabled={emailBroadcastBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  setEmailBroadcastBusy(true)
                  setEmailError(null)
                  try {
                    await queueCreatorBroadcast({
                      subject: emailSubject.trim(),
                      body_text: emailBody.trim() || undefined,
                    })
                    setEmailSubject('')
                    setEmailBody('')
                    await loadEmail()
                    setToast({ visible: true, emoji: 'OK', label: 'Campaign queued.', kind: 'success' })
                    setEmailConfirmOpen(false)
                  } catch (e: any) {
                    setEmailError(e?.response?.data?.detail || e?.message || 'Failed to queue broadcast')
                  } finally {
                    setEmailBroadcastBusy(false)
                  }
                }}
                className="rounded-xl bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
                disabled={emailBroadcastBusy || !emailSubject.trim() || Number(emailSummary?.broadcast_recipient_count ?? emailSummary?.subscribed ?? 0) <= 0}
              >
                {emailBroadcastBusy ? 'Queueing...' : 'Confirm send'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <GiftToast
        visible={toast.visible}
        emoji={toast.emoji}
        label={toast.label}
        detail={toast.detail}
        kind={toast.kind}
        onHide={() => setToast((t) => ({ ...t, visible: false }))}
        timeoutMs={2000}
      />

      <PreviewSubscriberExperienceModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        allowPPV={allowPPV}
        ppvPricePixe={defaultPPVInt ?? (settings?.default_ppv_price_pixe ?? null)}
        tiers={tiers}
      />
      <DisableConfirmModal
        open={disableOpen}
        onClose={() => setDisableOpen(false)}
        busy={saving}
        onConfirm={async () => {
          await patch({ is_enabled: false }, 'overview', 'Subscriptions disabled.')
          setDisableOpen(false)
        }}
      />
    </div>
  )
}

function MembershipFeaturesPanel({
  value,
  onChange,
}: {
  value: Pick<
    CreatorSubscriptionSettings,
    | 'enable_subscriber_comments'
    | 'enable_early_access'
    | 'enable_subscriber_badge'
    | 'enable_polls'
    | 'enable_bts_pixies'
  >
  onChange: (patch: Partial<typeof value>) => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold">Membership features</div>
          <div className="mt-1 text-xs text-white/60">Coming soon. These toggles are not wired yet.</div>
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-white/80 hover:bg-white/10"
          aria-expanded={open}
        >
          {open ? 'Hide' : 'Show'}
        </button>
      </div>
      {!open ? (
        <div className="mt-4 rounded-xl border border-white/10 bg-white/5 p-3 text-sm text-white/70">
          Keep pricing simple for V1. We will add subscriber perks here in a follow-up.
        </div>
      ) : (
        <div className="mt-4">
          <FeatureToggles value={value} onChange={onChange} />
        </div>
      )}
    </div>
  )
}
